import { Fab, Slider } from '@mui/material';
import { Box } from '@mui/system';
import * as d3 from 'd3';
import { GeoPath } from 'd3';
import i18next from 'i18next';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiArrowFromBottom } from 'react-icons/bi';
import { DropDownItem } from '../../models/DropDownItem';
import { SelectedGender } from '../../models/SelectedGender';
import { Element } from '../../models/zoneDectionary';
import { SelectedItem } from '../../pages/evda/evdaDepartmentsWidget';
import { MapPoint } from '../../service/MapPoint';
import { mobileBreakpoint, useWindowWidthAndHeight } from '../../shared/hooks/customHooks';
import { drawPopup, getPopupTopLeftPoint } from '../../shared/utils/mapControls';
import {
  getAvailableDataCount,
  getGraduationClass,
  getZoneQuantileGraduation,
  isNumeric,
  provideLegendHeight,
  providePadding,
  providePathClassName,
  round,
  sortDataByEvda,
} from '../../shared/utils/mapsUtils';
import { convertNumberToString } from '../../shared/utils/numbersUtils';
import countriesJSON from './europeData';
import departmentJSON from './franceData';
import './mapStyle.css';

export type Props = {
  orientation: 'landscape' | 'portrait';
  map: typeof departmentJSON | typeof countriesJSON;
  // Getter de l'id du noeud (département ou pays)
  nodeIdGetter: (node: any) => string;
  // Getter du nom du noeud
  nodeNameGetter: (node: any) => string;
  // Initie la projection avec le bon centrage et scale initial, spécifique à chaque carte
  projectionGetter: (width: number, height: number) => d3.GeoConicProjection;
  // Dessine les noeuds de la carte avec leur spécificités
  drawNodes: (
    path: GeoPath<any, any>,
    deps: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
    dataToDisplay: MapPoint[],
    selectedGender: SelectedGender
  ) => void;
  dictionary: Element[];
  data: MapPoint[];
  selectedItems: SelectedItem[];
  selectedGender: SelectedGender;
  itemClickHandler: (item: DropDownItem) => void;
  dontDisplayRegionKey?: boolean;
};

const MapWidget: React.FC<Props> = ({
  orientation,
  map,
  nodeIdGetter,
  nodeNameGetter,
  projectionGetter,
  drawNodes,
  dictionary,
  data,
  selectedItems,
  selectedGender,
  itemClickHandler,
  dontDisplayRegionKey,
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [width] = useWindowWidthAndHeight();
  const [zoomPercent, setZoomPourcent] = useState<number>(0);
  const [isSvgLoaded, setSvgState] = React.useState<boolean>(false);
  const [svgOrientation, setSvgOrientation] = useState<'landscape' | 'portrait'>('portrait');
  const { t, i18n } = useTranslation();
  const defaultFValue = useRef<number | undefined>(undefined);
  const mapContainerWidth = mapContainerRef.current?.offsetWidth || 0;
  const mapContainerHeight = mapContainerRef.current?.offsetHeight || 0;
  const graphWidth = mapContainerWidth - providePadding(mapContainerWidth);

  const zoom = d3.zoom().scaleExtent([1, 16]).on('zoom', zoomed);

  // Dessin de la légende
  function setGraduationValues(dataToDisplay: MapPoint[]) {
    // Génération des libellés des graduations, dans le sens décroissant
    var graduations = getZoneQuantileGraduation(dataToDisplay.filter((x) => isNumeric(x.esperance_de_vie))).reverse();

    for (var i = 0; i <= graduations.length; i++) {
      const label = graduations[i];
      // Maj du libellé i
      var text = d3.select('#labels-q' + i + '-5');
      text.text(convertNumberToString(label, i18n, 1, 1));
      // Maj du rectangle de couleur i
      let rect = d3.select('#legend-q' + i + '-5');
      rect.attr('class', providePathClassName(selectedGender) + i + '-5');
    }

    // Maj du titre de la légende
    let legendTitle = d3.select('#legendTitle');
    legendTitle.text(t('year_unit_plural', { postProcess: 'plural' }));
  }

  // Translation des popups
  function translatePopups() {
    if (selectedItems.length > 0) {
      selectedItems.forEach((item) => {
        const popupGroup = d3.select('#popup-g-' + item.key);
        // Place the popup over the region element.
        const popupTopLeftPoint = getPopupTopLeftPoint(item.key, width < mobileBreakpoint, defaultFValue.current);
        popupGroup.attr('transform', `translate(${popupTopLeftPoint.x},${popupTopLeftPoint.y})`);
      });
    }
  }

  // Masque les tooltip au survol
  function hideTooltip() {
    getHoverTooltip().html('').transition().duration(100).style('opacity', 0);
  }

  // Actualisation de la carte avec le jeu de données actualisé
  function setPathDetails(dataToDisplay: MapPoint[]) {
    map.features.forEach(function (feature) {
      const id = nodeIdGetter(feature);
      const donnee = dataToDisplay.find((datum) => datum.node === id);

      // Espérance de vie (possiblement NaN)
      const esperance = donnee ? parseFloat(donnee.esperance_de_vie) : NaN;
      // Classe de couleur
      const graduationClass = Number.isNaN(esperance)
        ? 'region q0-5'
        : providePathClassName(selectedGender) + getGraduationClass(dataToDisplay, esperance) + '-5';
      // Contenu HTML de la tooltip
      const tooltipContent = Number.isNaN(esperance)
        ? '<b>' + nodeNameGetter(feature) + '<br><br>' + t('value_not_available') + '</b>'
        : '<b>' +
          nodeNameGetter(feature) +
          '<br><br>' +
          convertNumberToString(esperance, i18n, 1, 1) +
          ' ' +
          i18next.t('year_unit_interval', { postProcess: 'interval', count: esperance }) +
          '</b>';

      // Actualisation du noeud
      d3.select('#d' + id)
        // Actualisation de la couleur
        .attr('class', graduationClass)
        // Affichage de la tooltip au mouseover
        .on('mouseover', (event, d) => {
          const tooltipDiv = getHoverTooltip();
          tooltipDiv
            .transition()
            .duration(200)
            .style('opacity', 0.9)
            .style('background', '#4D4F52')
            .style('left', event.pageX - 40 + 'px')
            .style('top', event.pageY - 80 + 'px');
          // Découpler le set du HTML de la transition est essentiel pour éviter
          // les interférences entre les différents évènements d'ouverture et de fermeture des tooltip.
          tooltipDiv.html(tooltipContent);
        })
        // Masquage de la tooltip au scroll
        .on('scroll', hideTooltip)
        // Masquage de la tooltip au sortir du curseur
        .on('mouseout', hideTooltip)
        // Remplacement de la tooltip au clic
        .on('click', function (event, d) {
          hideTooltip();
          const zone = dictionary.filter((element) => element.key === id)[0];
          itemClickHandler({ Element: zone, selected: true });
        });
    });
  }

  // Gestion du zoom
  function zoomed(e: any) {
    const svg = d3.select('#map-elements');

    // Si le facteur de zoom vaut 1 (pas de zoom)
    if (e.transform.k === 1) {
      // alors on recentre la carte
      e.transform.x = 0;
      e.transform.y = 0;
    }
    // Application de la matrice de transformation
    svg.attr('transform', e.transform);

    // Mise à jour du pourcentage de zoom affiché sur la réglette
    if (e.sourceEvent !== null) {
      let zoomVal = round(e.transform.k, 0.5);
      setZoomPourcent((zoomVal - 1) * 10 * 2);
    }

    // Translation des popups en conséquence du zoom
    translatePopups();
  }

  function clearMap() {
    d3.select('#svg').remove();
    d3.select('body > div.tooltip').remove();
  }

  function buildMap(dataToDisplay: MapPoint[]) {
    clearMap();

    const path: GeoPath<any, any> = d3.geoPath();

    // Initialisation de la projection géo-conique
    path.projection(projectionGetter(graphWidth, mapContainerHeight));

    // Bloc de tooltip au survol
    d3.select('body').append('div').attr('class', 'tooltip').style('opacity', 0);

    // Création de la balise SVG contenant la carte
    const svg = d3
      .select('#map')
      .append('svg')
      .attr('id', 'svg')
      .attr('role', 'img')
      .attr('alt', t('charts_aria-label'))
      .attr('aria-label', t('charts_aria-label'))
      .attr('width', graphWidth)
      .attr('height', mapContainerHeight)
      .attr('class', 'Blues')
      .attr('viewBox', '0 0 ' + graphWidth + ' ' + mapContainerHeight)
      .attr('preserveAspectRatio', 'xMinYMid');

    // Création du conteneur des noeuds
    const deps = svg.append('g').attr('id', 'map-elements');

    // Initialisation des noeuds
    drawNodes(path, deps, dataToDisplay, selectedGender);

    const legendTitleLineHeight = 19;

    // Création du conteneur des rectangles de la légende
    var legend = svg
      .append('g')
      .attr('transform', 'translate(' + graphWidth + ', 0)')
      .attr('id', 'legend');

    // Initialisation des rectangles de légende
    legend
      .selectAll('.colorbar')
      .data(d3.range(5))
      .enter()
      .append('svg:rect')
      .attr('y', (d) => legendTitleLineHeight + d * provideLegendHeight(graphWidth) + 'px')
      .attr('height', provideLegendHeight(graphWidth) + 'px')
      .attr('width', '13px')
      .attr('x', '-13px')
      // (4 - d) : On affiche désormais les légendes dans l'ordre décroissant, la valeur la plus haute en haut.
      .attr('id', (d) => 'legend-q' + (4 - d) + '-5');

    // Création du conteneur des libellés de la légende
    var labels = svg
      .append('g')
      .attr('transform', 'translate(' + graphWidth + ',' + provideLegendHeight(graphWidth) + ')')
      .attr('id', 'labels');

    // Initialisation des libellés de légende
    labels
      .selectAll('.colorbar')
      .data(d3.range(5))
      .enter()
      .append('svg:text')
      .attr('x', '-33px')
      .attr('height', '13px')
      .attr('width', '10px')
      .attr('y', (d) => legendTitleLineHeight + d * provideLegendHeight(graphWidth) + 'px')
      .attr('fill', '#2D363F')
      .style('font', 'normal normal normal 16px/19px Open Sans')
      .style('font-size', 'xx-small')
      .attr('id', (d) => 'labels-q' + d + '-5');

    // Création du titre de la légende
    const legendTitle = svg
      .append('svg:text')
      .attr('transform', 'translate(' + graphWidth + ',0)')
      .attr('id', 'legendTitle')
      .attr('fill', '#2D363F')
      .attr('y', '16px')
      .style('font', 'normal normal normal 16px/19px Open Sans')
      .style('font-size', 'small');
    legendTitle.text(t('year_unit_plural', { postProcess: 'plural' }));
    // On aligne la droite du titre de la légende avec la droite de la légende
    const largeurLegendTitle = (legendTitle.node() as SVGGeometryElement).getBBox().width;
    legendTitle.attr('x', '-' + largeurLegendTitle + 'px');

    // Copyright
    const copyright = svg
      .append('g')
      .append('svg:text')
      .text('©IGN – Insee 2023')
      .attr('fill', '#2D363F')
      .style('font', 'normal normal normal 16px/19px Open Sans')
      .style('font-size', 'xx-small')
      .attr('transform', 'rotate(90)')
      .attr('id', 'copyright');
    // On colle le copyright en bas à droite
    const largeurCopyright = (copyright.node() as SVGGeometryElement).getBBox().width;
    const hauteurCopyright = 15;
    copyright
      .attr('x', mapContainerHeight - largeurCopyright + 'px')
      // 15px : largeur du copyright
      .attr('y', '-' + (graphWidth - hauteurCopyright) + 'px');

    // Récupération du paramètre f de la matrix de transformation (CTM) des départements
    defaultFValue.current = deps.node()?.getCTM()?.f;
  }

  // Dessin des popups au clic
  function drawPopups() {
    const svg = d3.select('#svg');

    svg.selectAll('#popup-g').remove();

    if (selectedItems.length > 0) {
      let availableDataCount = getAvailableDataCount(data);
      let selectedRegionsToDraw = [];
      if (width < 1150) {
        selectedRegionsToDraw = [selectedItems[selectedItems.length - 1]];
      } else {
        selectedRegionsToDraw = [...selectedItems];
      }
      selectedRegionsToDraw.forEach(function (region: any) {
        const path = d3.select('#d' + region.key);
        const pathEl: SVGGeometryElement = path.node() as SVGGeometryElement;
        const bbox = pathEl.getBBox();
        const regionName = dontDisplayRegionKey ? region.name : region.name + ' (' + region.key + ')';
        const global = svg.append('g').attr('id', 'popup-g');
        if (region.esperance_de_vie !== 'nd' && region.esperance_de_vie !== 'NaN') {
          drawPopup(
            global,
            bbox.x + bbox.width / 2,
            bbox.y + bbox.height / 2,
            region.key,
            regionName,
            convertNumberToString(parseFloat(region.esperance_de_vie), i18n, 1, 1),
            i18next.t('year_unit_interval', { postProcess: 'interval', count: parseFloat(region.esperance_de_vie) }),
            t('rank').toString() + ' ' + region.rang + ' ' + t('of') + ' ' + availableDataCount,
            width < 600,
            defaultFValue.current
          );
        }
      });
    }
  }

  function getHoverTooltip(): d3.Selection<d3.BaseType, unknown, HTMLElement, any> {
    return d3.select('body > div.tooltip');
  }

  // Handler du zoom à la réglette
  const handleZoomChange = (event: Event, newValue: number | number[]) => {
    const svg: any = d3.select('#svg');
    // Valeur en pourcentage (0 à 300)
    const newVal = newValue as number;

    // Application du zoom avec un facteur 1/20
    // La valeur de zoom affiché et le niveau réel de zoom ne correspondent pas
    // D'après la réglette, on zoome de 1 (0%) à 4 fois (300%)
    // Alors que le facteur de zoom va en réalité de 1 à 16;
    svg
      .transition()
      .duration(700)
      .call(zoom.scaleTo, newVal / 20 + 1);

    setZoomPourcent(newValue as number);
  };

  const reinitMap = () => {
    if (mapContainerWidth !== 0 && data.length > 0) {
      const dataToDisplay = sortDataByEvda(data);
      if (!isSvgLoaded || svgOrientation !== orientation) {
        buildMap(dataToDisplay);
        setSvgState(true);
        setSvgOrientation(orientation);
      }
      setPathDetails(dataToDisplay);
      setGraduationValues(dataToDisplay);
    }
  };

  // Dessin et redessin de la carte après chargement des données et
  // changement du jeu de données
  useEffect(() => {
    reinitMap();
  }, [data, selectedGender, orientation]);

  // Dessin et màj des popups de la carte
  useEffect(() => {
    if (data.length > 0) {
      reinitMap();
      setTimeout(() => {
        drawPopups();
        const mapElements = d3.select('#svg');
        mapElements.call(zoom as any);
      }, 0);
    }
  }, [selectedItems, data]);

  // Mise à jour du titre de la légende avec un changement de langue
  useEffect(() => {
    if (data.length > 0) {
      setGraduationValues(sortDataByEvda(data));
    }
  }, [t]);

  useEffect(() => {
    setSvgDimensions();
    d3.geoPath().projection(projectionGetter(graphWidth, mapContainerHeight));
  }, [graphWidth]);

  function setSvgDimensions() {
    const svg = d3.select('#svg');
    svg.attr('width', graphWidth);
    svg.attr('height', mapContainerHeight);
  }

  const redirectTop = () => {
    window.scrollTo({
      top: 0,
    });
  };

  return (
    <div className='map-container'>
      <div id='map' ref={mapContainerRef}></div>

      {width <= mobileBreakpoint ? (
        <Fab
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
          }}
          aria-label={'Retour en haut de page'}
          color={'primary'}
          onClick={redirectTop}
        >
          <BiArrowFromBottom size={24} />
        </Fab>
      ) : undefined}

      <div className='zoom-desc'>
        <div className='zoom-label'>
          <span>Zoom : </span>
          <span>{zoomPercent + ' %'}</span>
        </div>
        <Box
          className='zoom-slider'
          sx={{
            width: 252,
            '& .MuiSlider-thumb': {
              borderRadius: '50px',
              borderWidth: '50px',
              width: '17px',
              height: '17px',
              border: '2px solid #286AC7',
              color: '#ffffff',
            },
          }}
        >
          <Slider
            min={0}
            max={300}
            value={zoomPercent}
            onChange={handleZoomChange}
            aria-label='Niveau de zoom de la carte'
            valueLabelDisplay='off'
            step={10}
          />
        </Box>
      </div>
    </div>
  );
};

export default MapWidget;
